import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  StickyNote,
  Lock,
  LinkIcon,
  Paperclip,
  Plus,
  X,
  Download,
  Trash2,
  Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  plannerAccess,
  listPlannerEvents,
  createPlannerEvent,
  updatePlannerEvent,
  uploadPlannerAttachment,
  deletePlannerAttachment,
  attachmentUrl,
} from "../lib/api";

const parseISO = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const fmtLong = (d) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const prettySize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function PlannerPeriod() {
  const { date, period } = useParams();
  const navigate = useNavigate();
  const periodLabel = useMemo(() => decodeURIComponent(period || ""), [period]);
  const fileRef = useRef(null);
  const saveTimer = useRef(null);

  const [access, setAccess] = useState({ has_access: false, loading: true });
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null); // period_note PlannerEvent (may be null until first save)
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [links, setLinks] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const dateObj = useMemo(() => parseISO(date), [date]);

  useEffect(() => {
    plannerAccess()
      .then((res) => setAccess({ ...res, loading: false }))
      .catch(() => setAccess({ has_access: false, loading: false }));
  }, []);

  useEffect(() => {
    if (!access.has_access) return;
    setLoading(true);
    listPlannerEvents(date, date)
      .then((list) => {
        const ev = (list || []).find((e) => e.kind === "period_note" && e.period_label === periodLabel);
        setEvent(ev || null);
        setNotes(ev?.notes || "");
        setSavedNotes(ev?.notes || "");
        setLinks(Array.isArray(ev?.links) ? ev.links : []);
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [access.has_access, date, periodLabel]);

  // Create the backing period_note event on first write.
  const ensureEvent = useCallback(async () => {
    if (event) return event;
    const ev = await createPlannerEvent({
      date,
      period_label: periodLabel,
      kind: "period_note",
      activity_name: "Period",
    });
    setEvent(ev);
    return ev;
  }, [event, date, periodLabel]);

  // Debounced notes auto-save.
  useEffect(() => {
    if (loading || !access.has_access) return undefined;
    if (notes === savedNotes) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        const ev = await ensureEvent();
        await updatePlannerEvent(ev.id, { notes });
        setSavedNotes(notes);
      } catch {
        toast.error("Could not save notes.");
      } finally {
        setSavingNotes(false);
      }
    }, 700);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [notes, savedNotes, loading, access.has_access, ensureEvent]);

  const persistLinks = async (next) => {
    setLinks(next);
    try {
      const ev = await ensureEvent();
      await updatePlannerEvent(ev.id, { links: next });
    } catch {
      toast.error("Could not save link.");
    }
  };

  const addLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    persistLinks([...links, { url: normalized, label: linkLabel.trim() }]);
    setLinkUrl("");
    setLinkLabel("");
  };
  const removeLink = (idx) => persistLinks(links.filter((_, i) => i !== idx));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const ev = await ensureEvent();
      const updated = await uploadPlannerAttachment(ev.id, file, setProgress);
      setEvent(updated);
      toast.success("Attachment added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = async (attachmentId) => {
    if (!event) return;
    try {
      await deletePlannerAttachment(event.id, attachmentId);
      setEvent((ev) => ({ ...ev, attachments: (ev.attachments || []).filter((a) => a.id !== attachmentId) }));
      toast.success("Attachment removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove.");
    }
  };

  if (access.loading) {
    return <p className="text-[#4a6984]/70 p-6" data-testid="period-loading">Loading…</p>;
  }
  if (!access.has_access) {
    return (
      <div className="ae-card p-10 text-center max-w-xl mx-auto mt-8" data-testid="period-upsell">
        <Lock className="h-9 w-9 mx-auto text-[#4a6984]/70 mb-3" />
        <h3 className="font-extrabold text-xl text-[#1c2d3d] mb-2">Planner is a Pro feature</h3>
        <Link to="/pricing">
          <Button className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold">Upgrade to Pro</Button>
        </Link>
      </div>
    );
  }

  const attachments = event?.attachments || [];

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4" data-testid="period-root">
      <Link
        to={`/dashboard?view=planner&date=${date}`}
        className="text-sm font-semibold text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1"
        data-testid="period-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to planner
      </Link>

      <div className="ae-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-[#96ded1]/40 text-[#1c2d3d] border border-[#96ded1]">
            <Clock className="h-3.5 w-3.5" /> {periodLabel}
          </span>
        </div>
        <h2 className="font-extrabold text-2xl text-[#1c2d3d]" data-testid="period-date">{fmtLong(dateObj)}</h2>
        {event?.note && <p className="text-sm text-[#4a6984] mt-0.5">{event.note}</p>}

        {loading ? (
          <div className="flex items-center gap-2 text-[#4a6984] py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="mt-5 space-y-7">
            {/* Notes */}
            <section data-testid="period-notes-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1.5 mb-2">
                <StickyNote className="h-3.5 w-3.5" /> Notes
              </h3>
              <Textarea
                rows={8}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={"e.g.\n• Split class into 3 stations\n• Sub instructions: hand out worksheet on desk"}
                className="text-sm leading-relaxed"
                data-testid="period-notes-textarea"
              />
              <p className="text-[11px] text-[#4a6984]/60 mt-1 h-4" data-testid="period-notes-status">
                {savingNotes ? "Saving…" : notes === savedNotes ? "All changes saved." : ""}
              </p>
            </section>

            {/* Links */}
            <section data-testid="period-links-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1.5 mb-2">
                <LinkIcon className="h-3.5 w-3.5" /> Links
              </h3>
              <div className="space-y-1.5 mb-3">
                {links.length === 0 && <p className="text-xs text-[#4a6984]/60 italic">No links yet.</p>}
                {links.map((l, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 rounded-md border border-[#4a6984]/12 bg-white px-3 py-2"
                    data-testid={`period-link-${idx}`}
                  >
                    <LinkIcon className="h-3.5 w-3.5 text-[#4a6984]/60 shrink-0" />
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1c2d3d] hover:text-[#1c9e7e] font-semibold truncate flex-1">
                      {l.label || l.url}
                    </a>
                    <button type="button" onClick={() => removeLink(idx)} className="text-[#c45a6b] hover:bg-[#c45a6b]/5 rounded p-1" aria-label="Remove link" data-testid={`period-link-remove-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                <Input placeholder="https://…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid="period-link-url" />
                <Input placeholder="Optional label" value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} data-testid="period-link-label" />
                <Button onClick={addLink} disabled={!linkUrl.trim()} className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white" data-testid="period-link-add">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
            </section>

            {/* Attachments */}
            <section data-testid="period-attachments-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1.5 mb-2">
                <Paperclip className="h-3.5 w-3.5" /> Attachments
              </h3>
              <div className="space-y-1.5 mb-3">
                {attachments.length === 0 && <p className="text-xs text-[#4a6984]/60 italic">No files attached yet.</p>}
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 rounded-md border border-[#4a6984]/12 bg-white px-3 py-2" data-testid={`period-attachment-${a.id}`}>
                    <Paperclip className="h-3.5 w-3.5 text-[#4a6984]/60 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1c2d3d] font-semibold truncate">{a.original_filename}</div>
                      <div className="text-[11px] text-[#4a6984]/60">{prettySize(a.size)}</div>
                    </div>
                    <a href={attachmentUrl(event.id, a.id)} target="_blank" rel="noopener noreferrer" className="text-[#1c9e7e] hover:bg-[#96ded1]/15 rounded p-1" title="Download" data-testid={`period-attachment-download-${a.id}`}>
                      <Download className="h-4 w-4" />
                    </a>
                    <button type="button" onClick={() => removeAttachment(a.id)} className="text-[#c45a6b] hover:bg-[#c45a6b]/5 rounded p-1" aria-label="Remove attachment" data-testid={`period-attachment-remove-${a.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="period-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="period-upload-btn">
                {uploading ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading… {progress}%</>
                ) : (
                  <><Plus className="h-3.5 w-3.5 mr-1" /> Add attachment</>
                )}
              </Button>
              <p className="text-[11px] text-[#4a6984]/60 mt-1">PDFs, images, Word docs — up to 25 MB each.</p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
